import { useCallback, useEffect, useState } from "react";
import type { ReportGroupApiClient } from "../../../../../../../../api/biApi";
import type { ReportGroup } from "../../../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../../../context/useClientContext.ts";

interface Props {
  apiClient: ReportGroupApiClient;
}
export default function useReportGroups({ apiClient }: Props) {
  const { clientCode } = useClientContext();
  const [reportGroups, setReportGroups] = useState<ReportGroup[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState<boolean>(false);

  const loadReportGroups = useCallback(
    async (clientCode: string) => {
      try {
        const groupsRes = await apiClient.getAccessibleReportGroups(clientCode);
        if (groupsRes.success === false) {
          setError(groupsRes.error?.message);
          return;
        }
        setReportGroups(groupsRes.data);
      } catch {
        setError("Report groups loading failed");
      } finally {
        setLoaded(true);
      }
    },
    [apiClient]
  );

  useEffect(() => {
    // TODO #209
    loadReportGroups(clientCode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const reloadReportGroups = useCallback(async () => {
    const resp = await apiClient.getAccessibleReportGroups(clientCode);
    if (resp.success) {
      setReportGroups(resp.data);
    }
  }, [clientCode, apiClient]);

  const addReportGroup = useCallback(
    async (clientCode: string, group: ReportGroup) => {
      const resp = await apiClient.addReportGroup({ clientCode, group });
      if (resp.success) {
        await reloadReportGroups();
        return resp.data;
      }
      throw new Error(resp.error?.message);
    },
    [reloadReportGroups, apiClient]
  );

  const updateReportGroup = useCallback(
    async (clientCode: string, group: ReportGroup) => {
      const resp = await apiClient.updateReportGroup({ clientCode, group });
      if (resp.success) {
        await reloadReportGroups();
        return;
      }
      throw new Error(resp.error?.message);
    },
    [reloadReportGroups, apiClient]
  );

  const deleteReportGroup = useCallback(
    async (clientCode: string, groupId: string, reassignToGroupId?: string) => {
      const resp = await apiClient.deleteReportGroup(clientCode, groupId, reassignToGroupId);
      if (!resp.success) {
        throw new Error(resp.error?.message);
      }
      if (resp.data.status === "Deleted") {
        await reloadReportGroups();
      }
      return resp.data;
    },
    [reloadReportGroups, apiClient]
  );

  const replaceReportGroup = useCallback(
    async (clientCode: string, fromGroup: string, toGroup: string) => {
      const resp = await apiClient.replaceReportGroup({ clientCode, fromGroup, toGroup });
      if (resp.success) {
        return resp.data;
      }
      throw new Error(resp.error?.message);
    },
    [apiClient]
  );

  const reorderReportGroups = useCallback(
    async (clientCode: string, groups: ReportGroup[]) => {
      const resp = await apiClient.reorderReportGroups({ clientCode, groups });
      if (resp.success) {
        setReportGroups(resp.data);
        return;
      }
      throw new Error(resp.error?.message);
    },
    [apiClient]
  );

  return {
    reportGroups,
    ui: { error, loaded },
    actions: {
      add: addReportGroup,
      update: updateReportGroup,
      delete: deleteReportGroup,
      replace: replaceReportGroup,
      reorder: reorderReportGroups,
      load: reloadReportGroups,
    },
  };
}
